import React, { useState, useEffect, FC } from "react";
import { Button as RButton } from "reactstrap";
import {
  Table,
  Input,
  Flex,
  Spin,
  Tooltip,
  Select,
  Space,
  Tag,
  Empty,
  Card,
  Button,
  Drawer,
  Form,
  Badge,
  Row,
  Col,
} from "antd";
import { Icon, TooltipComponent } from "../../components/Component";
import FormatDate from "../masters/DateFormater";
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  UnlockOutlined,
  EyeOutlined,
  SaveOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import PaymentsAside from "./PaymentsAside";
import ReceivablesAside from "./ReceivablesAside";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";
import { formatCurrency } from "../../utils/Utils";
import dayjs from "dayjs";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import { useSelector } from "react-redux";

// Filter State Interface
interface FilterState {
  siteId?: string[];
  contractorName?: string[];
  workOrderId?: string[];
}

// Interface for On Hold Data
interface OnHoldWorkOrderData {
  workOrderId: string;
  workOrderNumber: string;
  contractorId: string;
  contractorName: string;
  workOrderValue: number;
  holdAmountHeld: number;
  holdReleased: number;
  holdBalance: number;
  status: "Eligible" | "On Hold" | "Partially Released" | "Fully Released";
}

const ReleaseOnHoldList: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const flowType = searchParams.get("flowType");
  const [sm, setSm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [onHoldData, setOnHoldData] = useState<OnHoldWorkOrderData[]>([]);
  const [filteredData, setFilteredData] = useState<OnHoldWorkOrderData[]>([]);

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [selectedContractor, setSelectedContractor] = useState<
    string | undefined
  >();
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
  const [contractorOptions, setContractorOptions] = useState<any[]>([]);
  const [contractorLoading, setContractorLoading] = useState(false);

  // Advanced Filters State
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [savedFilters, setSavedFilters] = useState<{
    [key: string]: FilterState;
  }>({});
  const [filterForm] = Form.useForm();
  const [siteOptions, setSiteOptions] = useState<any[]>([]);
  const [workOrderOptions, setWorkOrderOptions] = useState<any[]>([]);
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const isClientFlow = flowType?.toLowerCase() === "client";

  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  useEffect(() => {
    // loadContractorOptions();
    // loadOnHoldData();
  }, []);

  // Fetch site options from currentUser
  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(advancedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === "string" && value.trim() !== "") count++;
      }
    });
    return count;
  };

  // Filter Functions
  const handleFilterDrawerOpen = async () => {
    setTempFilters({ ...advancedFilters });
    filterForm.setFieldsValue(advancedFilters);
    setFilterDrawerVisible(true);

    // Fetch dropdown data when drawer opens
    try {
      const flowTypeParam = isClientFlow ? "Client" : "Contractor";

      // Fetch contractors
      const partyType = isClientFlow ? "Client" : "Contractor";
      const contractorsResponse = await dropdownApis.fetchContractorDropDown(
        partyType
      );
      if (
        contractorsResponse &&
        contractorsResponse.status &&
        contractorsResponse.data
      ) {
        const contractorOptions = contractorsResponse.data.map((item: any) => ({
          label: item.companyName || "N/A",
          value: item.contractorID?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }

      // Fetch work orders
      const workOrdersResponse = await dropdownApis.fetchWorkOrderDropdown(
        flowTypeParam
      );
      if (
        workOrdersResponse &&
        workOrdersResponse.status &&
        workOrdersResponse.data
      ) {
        const workOrderOptions = workOrdersResponse.data.map((item: any) => {
          const label =
            item.workOrderNumber ||
            item.workOrderSequenceId ||
            item.sequenceId ||
            item.workOrderTitle ||
            `WO-${item.workOrderId || item.id || "N/A"}`;
          const value = item.workOrderId?.toString() || item.id?.toString();
          return { label, value };
        });
        setWorkOrderOptions(workOrderOptions);
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const handleFilterDrawerClose = () => {
    setFilterDrawerVisible(false);
    setTempFilters({});
    filterForm.resetFields();
  };

  const handleApplyFilters = () => {
    filterForm
      .validateFields()
      .then((values) => {
        const processedFilters: FilterState = {
          siteId:
            values.siteId && values.siteId.length > 0
              ? values.siteId
              : undefined,
          contractorName:
            values.contractorName && values.contractorName.length > 0
              ? values.contractorName
              : undefined,
          workOrderId:
            values.workOrderId && values.workOrderId.length > 0
              ? values.workOrderId
              : undefined,
        };

        // Remove undefined values
        Object.keys(processedFilters).forEach((key) => {
          if (processedFilters[key as keyof FilterState] === undefined) {
            delete processedFilters[key as keyof FilterState];
          }
        });

        // Always create a new object reference to trigger useEffect
        setAdvancedFilters({ ...processedFilters });
        setFilterDrawerVisible(false);
      })
      .catch((error) => {
        console.error("Form validation failed:", error);
      });
  };

  const handleResetFilters = () => {
    setAdvancedFilters({});
    setTempFilters({});
    filterForm.resetFields();
  };

  const handleSaveFilter = () => {
    const filterName = prompt("Enter a name for this filter:");
    if (filterName && filterName.trim()) {
      setSavedFilters((prev) => ({
        ...prev,
        [filterName]: { ...advancedFilters },
      }));
    }
  };

  const handleLoadFilter = (filterName: string) => {
    const savedFilter = savedFilters[filterName];
    if (savedFilter) {
      setAdvancedFilters(savedFilter);
      filterForm.setFieldsValue(savedFilter);
    }
  };

  // useEffect(() => {
  //   applyFilters();
  // }, [searchText, selectedContractor, selectedStatus, onHoldData]);

  const loadContractorOptions = async () => {
    setContractorLoading(true);
    try {
      const response = await dropdownApis.fetchContractorDropDown();
      if (response.status && response.data) {
        const options = response.data.map((contractor: any) => ({
          value: contractor.contractorID.toString(),
          label: contractor.companyName,
        }));
        setContractorOptions(options);
      }
    } catch (error) {
      console.error("Error loading contractor options:", error);
    } finally {
      setContractorLoading(false);
    }
  };

  // const loadOnHoldData = async () => {
  //   setLoading(true);
  //   try {
  //     // TODO: Replace with actual API call
  //     // const response = await paymentApis.getOnHoldList();

  //     // Mock data for now
  //     const mockData: OnHoldWorkOrderData[] = [
  //       {
  //         workOrderId: "1",
  //         workOrderNumber: "WO/2024/001",
  //         contractorId: "1",
  //         contractorName: "ABC Constructions Pvt Ltd",
  //         workOrderValue: 5000000,
  //         holdAmountHeld: 500000,
  //         holdReleased: 0,
  //         holdBalance: 500000,
  //         status: "Eligible",
  //       },
  //       {
  //         workOrderId: "2",
  //         workOrderNumber: "WO/2024/002",
  //         contractorId: "2",
  //         contractorName: "XYZ Infrastructure Ltd",
  //         workOrderValue: 8000000,
  //         holdAmountHeld: 800000,
  //         holdReleased: 200000,
  //         holdBalance: 600000,
  //         status: "Partially Released",
  //       },
  //       {
  //         workOrderId: "3",
  //         workOrderNumber: "WO/2024/003",
  //         contractorId: "1",
  //         contractorName: "ABC Constructions Pvt Ltd",
  //         workOrderValue: 3000000,
  //         holdAmountHeld: 300000,
  //         holdReleased: 0,
  //         holdBalance: 300000,
  //         status: "On Hold",
  //       },
  //     ];

  //     setOnHoldData(mockData);
  //     setFilteredData(mockData);
  //     setPagination((prev) => ({ ...prev, total: mockData.length }));
  //   } catch (error) {
  //     console.error("Error loading on hold data:", error);
  //     Swal.fire("Error!", "Failed to load on hold data", "error");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // const applyFilters = () => {
  //   let filtered = [...onHoldData];

  //   // Search filter
  //   if (searchText) {
  //     const search = searchText.toLowerCase();
  //     filtered = filtered.filter(
  //       (item) =>
  //         item.workOrderNumber.toLowerCase().includes(search) ||
  //         item.contractorName.toLowerCase().includes(search)
  //     );
  //   }

  //   // Contractor filter
  //   if (selectedContractor) {
  //     filtered = filtered.filter(
  //       (item) => item.contractorId === selectedContractor
  //     );
  //   }

  //   // Status filter
  //   if (selectedStatus) {
  //     filtered = filtered.filter((item) => item.status === selectedStatus);
  //   }

  //   setFilteredData(filtered);
  //   setPagination((prev) => ({ ...prev, total: filtered.length, current: 1 }));
  // };

  const handleReleaseHold = (record: any) => {
    const flowTypeParam = flowType || "Contractor";
    navigate(
      `${process.env.PUBLIC_URL}/release-on-hold-form?flowType=${flowTypeParam}`,
      {
        state: {
          workOrderId: record.workOrderId,
          contractorId: record.contractorId,
          contractorName: record.contractorName,
          flowType: flowTypeParam,
          line: {
            WorkOrder: record.workOrderNumber,
            BillValue: record.totalRecoveredAmount,
            PaidtillDate: record.totalPaidAmount,
            Balance: record.balanceAmount,
            ThisPayment: record.balanceAmount,
            pendingApproval: record.totalPendingApprovalPaidAmount,
            siteId: record.siteId,
            siteName: record.siteName,
          },
        },
      }
    );
  };

  const clearFilters = () => {
    setSearchText("");
    setSelectedContractor(undefined);
    setSelectedStatus(undefined);
  };

  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      Eligible: "success",
      "On Hold": "warning",
      "Partially Released": "orange",
      "Fully Released": "cyan",
    };
    return colors[status] || "default";
  };

  const columns = [
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 250,
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title:
        flowType?.toLowerCase() === "client"
          ? "Client Name"
          : "Contractor Name",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 220,
      ellipsis: true,
    },
    {
      title: "WO Value",
      dataIndex: "grossValue",
      key: "grossValue",
      width: 140,
      render: (value: number) => formatCurrency(value, "INR"),
    },
    {
      title: "Hold Amount Held",
      dataIndex: "totalRecoveredAmount",
      key: "totalRecoveredAmount",
      width: 150,
      render: (value: number) => formatCurrency(value, "INR"),
    },
    {
      title: "Hold Released",
      dataIndex: "totalPaidAmount",
      key: "totalPaidAmount",
      width: 140,
      render: (value: number) => formatCurrency(value, "INR"),
    },
    {
      title: "Pending Approvals",
      dataIndex: "totalPendingApprovalPaidAmount",
      key: "totalPendingApprovalPaidAmount",
      width: 140,
      render: (value: number) => formatCurrency(value, "INR"),
    },
    {
      title: "Hold Balance",
      dataIndex: "balanceAmount",
      key: "balanceAmount",
      width: 140,
      render: (value: number) => (
        <strong style={{ color: value > 0 ? "#52c41a" : "#999" }}>
          {formatCurrency(value, "INR")}
        </strong>
      ),
    },
    {
      title: "Eligibility Date",
      dataIndex: "releaseDate",
      key: "releaseDate",
      width: 130,
      render: (date: string) => dayjs(date).format("DD-MMM-YYYY"),
    },
    // {
    //   title: "Status",
    //   dataIndex: "status",
    //   key: "status",
    //   width: 180,
    //   render: (status: string) => (
    //     <Tag color={getStatusColor(status)}>{status}</Tag>
    //   ),
    // },
    {
      title: "Action",
      key: "action",
      width: 150,
      fixed: "right" as const,
      render: (_: any, record: OnHoldWorkOrderData) => (
        <Space>
          <Tooltip title="Release Hold">
            <Button
              type="primary"
              size="small"
              icon={<UnlockOutlined />}
              onClick={() => handleReleaseHold(record)}
              disabled={
                record.status === "Fully Released" || record.holdBalance <= 0
              }
            >
              Release
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="release-on-hold-list"
        title="Release On Hold"
        columns={columns}
        loading={loading}
        customFilters={advancedFilters}
        topRightJSX={
          <Space>
            <Badge count={getActiveFilterCount()} size="small">
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={handleFilterDrawerOpen}
              >
                Advanced Filters
              </Button>
            </Badge>
          </Space>
        }
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            {flowType?.toLowerCase() === "client" ? (
              <ReceivablesAside updateSm={setSm} sm={sm} />
            ) : (
              <PaymentsAside updateSm={setSm} sm={sm} />
            )}
          </div>
        }
        extra={{
          tableScrollX: 1200,
        }}
      />

      {/* Advanced Filters Drawer */}
      <Drawer
        title="Advanced Filters"
        placement="right"
        width={420}
        open={filterDrawerVisible}
        onClose={handleFilterDrawerClose}
        extra={
          <Space size="middle">
            <Badge count={getActiveFilterCount()} size="small">
              <Button size="small" type="text" icon={<FilterOutlined />} />
            </Badge>
            <Button
              size="small"
              onClick={handleResetFilters}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
          </Space>
        }
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button
                size="small"
                onClick={handleSaveFilter}
                icon={<SaveOutlined />}
              >
                Save Filter
              </Button>
              {Object.keys(savedFilters).length > 0 && (
                <Select
                  placeholder="Load saved filter"
                  size="small"
                  style={{ width: 150 }}
                  suffixIcon={<FolderOpenOutlined />}
                  onChange={handleLoadFilter}
                  allowClear
                >
                  {Object.keys(savedFilters).map((filterName) => (
                    <Select.Option key={filterName} value={filterName}>
                      {filterName}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Space>
            <Space>
              <Button onClick={handleFilterDrawerClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleApplyFilters}
                icon={<SearchOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </div>
        }
      >
        <Form
          form={filterForm}
          layout="vertical"
          initialValues={advancedFilters}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="siteId" label="Site/Project">
                <Select
                  mode="multiple"
                  placeholder="Select Sites"
                  options={siteOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="contractorName"
                label={isClientFlow ? "Client" : "Contractor"}
              >
                <Select
                  mode="multiple"
                  placeholder={
                    isClientFlow ? "Select Clients" : "Select Contractors"
                  }
                  options={contractorOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderId" label="Work Order">
                <Select
                  mode="multiple"
                  placeholder="Select Work Orders"
                  options={workOrderOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
};

export default ReleaseOnHoldList;
